import { useMemo } from 'react';

import { useUserState } from 'providers/user-state-provider';

import {
  useIntegrationAdaptersQuery,
  usePartnerIntegrationsQuery,
} from 'lib/graphql';

import { ModuleId } from './module-id.enum';
import { anyPartnerAllowed, isPartnerAllowed } from './partner-allowance';

interface IntegrationsModuleConfig {
  allowedIntegrations?: string[];
}

/** A partner this vendor is actually connected to and sending to. */
export interface EnabledPartner {
  /** Registry key — the value stored in `partnerCodes[].adapter`. */
  key: string;
  /** Operator-facing name, for tab labels and headings. */
  label: string;
  /**
   * Label for this partner's secondary catalogue key, when it has one.
   *
   * Read off the adapter's first `map` config field rather than from a list of
   * partners here: a partner that needs a translation table to identify its
   * products is exactly a partner whose catalogue is two-dimensional, and the
   * table's own `valueLabel` already names the thing. So VistaTrac gets
   * "VistaTrac species code" without this file knowing VistaTrac exists.
   */
  scopeLabel?: string;
  /**
   * That table as configured for this vendor — our species → the partner's
   * code for it. Keyed by whatever the operator typed on the settings page.
   */
  scopeDefaults?: Record<string, string>;
  /**
   * Whether this processor has switched on per-item overrides of that table.
   *
   * Off by default and off for every partner that never declares the toggle,
   * so a surface offering the override stays hidden unless someone asked for
   * it. For VistaTrac the override is genuinely unusual — every line of an
   * order must carry the order's species — so the field would be clutter on
   * most catalogues and a trap on the rest.
   */
  scopeOverridable: boolean;
}

export interface PartnerIntegrationsState {
  /** Connected, enabled partners. Empty while loading, or when there are none. */
  partners: EnabledPartner[];
  loading: boolean;
}

/**
 * The partner platforms this vendor has connected AND switched on.
 *
 * "Enabled" is deliberately three conditions, not one: the vendor's
 * `INTEGRATIONS` module must allow `partners` at all, an `Integration` row must
 * exist for the adapter, and that row must be `enabled`. Anything less and the
 * vendor is not sending to that partner, so UI keyed off this should not appear
 * — the surfaces that use it (partner code mapping, for one) would otherwise
 * ask a processor to fill in codes nothing will ever read.
 *
 * Labels come from the integration row when the operator renamed it, and from
 * the adapter registry otherwise, so a tab says "PPS" rather than "pps".
 * @returns The enabled partners and whether the lookup is still in flight.
 */
export function usePartnerIntegrations(): PartnerIntegrationsState {
  const { getModuleConfig } = useUserState();
  const config = getModuleConfig<IntegrationsModuleConfig>(
    ModuleId.INTEGRATIONS,
  );
  const allowedIntegrations = config?.allowedIntegrations ?? [];
  const partnersAllowed = anyPartnerAllowed(allowedIntegrations);

  const { data: integrationData, loading: integrationsLoading } =
    usePartnerIntegrationsQuery({ skip: !partnersAllowed });
  const { data: adapterData, loading: adaptersLoading } =
    useIntegrationAdaptersQuery({ skip: !partnersAllowed });

  return useMemo(() => {
    if (!partnersAllowed) return { partners: [], loading: false };

    const adapters = new Map(
      (adapterData?.integrationAdapters ?? []).map((adapter) => [
        adapter?.key ?? '',
        adapter,
      ]),
    );

    const partners = (integrationData?.integrationMany ?? [])
      .filter((row) => row?.enabled && row?.adapter)
      // A connection the vendor is no longer allowed stays configured but
      // stops driving UI — so revoking one partner does not require anyone to
      // go and delete its row first.
      .filter((row) =>
        isPartnerAllowed(allowedIntegrations, String(row!.adapter)),
      )
      .map((row) => {
        const key = String(row!.adapter);
        const adapter = adapters.get(key);
        const scopeField = (adapter?.configFields ?? []).find(
          (field) => field?.type === 'map',
        );
        const config = (row!.config ?? {}) as Record<string, unknown>;
        return {
          key,
          label: row!.label || adapter?.label || key,
          scopeLabel: scopeField?.valueLabel ?? undefined,
          scopeDefaults: scopeField
            ? ((config[scopeField.key] as Record<string, string>) ?? {})
            : undefined,
          scopeOverridable: Boolean(
            scopeField?.overrideToggleKey &&
              config[scopeField.overrideToggleKey],
          ),
        };
      });

    return {
      partners,
      loading: integrationsLoading || adaptersLoading,
    };
  }, [
    allowedIntegrations,
    partnersAllowed,
    integrationData,
    adapterData,
    integrationsLoading,
    adaptersLoading,
  ]);
}
